import { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { clipsApi } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import AppLayout from '../components/AppLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Scissors, Eye, Heart, Share2, TrendingUp, Trophy, Clock, Play, DollarSign } from 'lucide-react';

const formatViews = (n) => {
  if (!n) return '0';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
};

const statusBadge = (s) => {
  const map = {
    published: 'bg-green-500/20 text-green-500',
    trending: 'bg-yellow-500/20 text-yellow-500',
    viral: 'bg-primary/20 text-primary',
  };
  return map[s] || 'bg-white/10 text-muted-foreground';
};

const ClipsPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('discover');
  const [clips, setClips] = useState([]);
  const [myClips, setMyClips] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [clipsRes, myClipsRes, earningsRes, campaignsRes] = await Promise.all([
        clipsApi.getAll(),
        clipsApi.getMy(),
        clipsApi.getEarnings(),
        clipsApi.getCampaigns(),
      ]);
      setClips(clipsRes.data);
      setMyClips(myClipsRes.data);
      setEarnings(earningsRes.data);
      setCampaigns(campaignsRes.data);
    } catch (error) {
      toast.error('Failed to load clips data');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className="p-6 lg:p-0 space-y-6">
          <Skeleton className="h-8 w-48" /><Skeleton className="h-48 w-full rounded-xl" /><Skeleton className="h-64 w-full rounded-xl" />
        </div>
      </AppLayout>
    );
  }

  const ClipCard = ({ clip }) => (
    <div className="bg-card border border-white/10 rounded-xl overflow-hidden hover:border-primary/30 transition-colors group" data-testid={`clip-${clip.clip_id}`}>
      <div className="relative aspect-video bg-black">
        <img src={clip.thumbnail_url} alt="" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center"><Play className="w-6 h-6 text-white ml-1" /></div>
        </div>
        <div className="absolute bottom-2 right-2 bg-black/70 px-2 py-0.5 rounded text-xs text-white">{clip.duration_seconds}s</div>
        <div className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs capitalize ${statusBadge(clip.status)}`}>{clip.status}</div>
      </div>
      <div className="p-4">
        <p className="font-medium line-clamp-2 mb-2 text-sm">{clip.title}</p>
        <p className="text-xs text-muted-foreground mb-3 truncate">from {clip.original_content_title || 'Original Content'}</p>
        <div className="flex items-center gap-2 mb-3">
          {clip.clipper_avatar && <img src={clip.clipper_avatar} alt="" className="w-5 h-5 rounded-full" />}
          <span className="text-xs text-muted-foreground">Clipped by <span className="text-foreground">{clip.clipper_name}</span></span>
        </div>
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{formatViews(clip.views)}</span>
          <span className="flex items-center gap-1"><Heart className="w-3 h-3" />{formatViews(clip.likes)}</span>
          <span className="flex items-center gap-1"><Share2 className="w-3 h-3" />{formatViews(clip.shares)}</span>
          <span className="ml-auto text-green-500 font-medium">{formatCurrency(clip.total_earnings)}</span>
        </div>
      </div>
    </div>
  );

  return (
    <AppLayout>
      <div className="min-h-screen lg:min-h-0 pb-24 lg:pb-0">
        {/* Header */}
        <div className="p-6 lg:p-0 lg:mb-6">
          <h1 className="text-2xl lg:text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Scissors className="w-7 h-7 inline mr-2 text-primary" />Clips
          </h1>
          <p className="text-muted-foreground">Clip, share, and earn from content</p>
        </div>

        <div className="lg:grid lg:grid-cols-3 lg:gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Earnings Summary */}
            {earnings && (
              <div className="px-6 lg:px-0 mb-6">
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-card border border-white/10 rounded-xl p-4">
                    <DollarSign className="w-5 h-5 text-green-500 mb-2" />
                    <p className="text-xs text-muted-foreground">Total Earnings</p>
                    <p className="text-lg font-bold text-green-500">{formatCurrency(earnings.total_earnings)}</p>
                  </div>
                  <div className="bg-card border border-white/10 rounded-xl p-4">
                    <Eye className="w-5 h-5 text-blue-500 mb-2" />
                    <p className="text-xs text-muted-foreground">Total Views</p>
                    <p className="text-lg font-bold">{formatViews(earnings.total_views)}</p>
                  </div>
                  <div className="bg-card border border-white/10 rounded-xl p-4">
                    <Scissors className="w-5 h-5 text-primary mb-2" />
                    <p className="text-xs text-muted-foreground">My Clips</p>
                    <p className="text-lg font-bold">{earnings.total_clips_created + earnings.total_clips_clipped}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Tabs */}
            <div className="px-6 lg:px-0">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-3 mb-6">
                  <TabsTrigger value="discover">Discover</TabsTrigger>
                  <TabsTrigger value="my-clips">My Clips</TabsTrigger>
                  <TabsTrigger value="campaigns">Challenges</TabsTrigger>
                </TabsList>

                <TabsContent value="discover">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {clips.slice(0, 12).map((clip) => <ClipCard key={clip.clip_id} clip={clip} />)}
                  </div>
                </TabsContent>

                <TabsContent value="my-clips">
                  {myClips.length === 0 ? (
                    <div className="text-center py-12 bg-card border border-white/10 rounded-xl">
                      <Scissors className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="font-semibold mb-2">No clips yet</h3>
                      <p className="text-muted-foreground text-sm">Start clipping content to earn money!</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {myClips.map((clip) => <ClipCard key={clip.clip_id} clip={clip} />)}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="campaigns" className="space-y-4">
                  {campaigns.map((campaign) => (
                    <div key={campaign.clip_campaign_id} className="bg-card border border-white/10 rounded-xl p-5 hover:border-primary/30 transition-colors" data-testid={`clip-campaign-${campaign.clip_campaign_id}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Trophy className="w-5 h-5 text-yellow-500" />
                            <h3 className="font-semibold">{campaign.title}</h3>
                          </div>
                          <p className="text-sm text-muted-foreground">{campaign.brand_name}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${campaign.status === 'ending_soon' ? 'bg-yellow-500/20 text-yellow-500' : 'bg-green-500/20 text-green-500'}`}>
                          {campaign.status === 'ending_soon' ? 'Ending Soon' : 'Active'}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">{campaign.description}</p>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-primary font-bold">Prize: {formatCurrency(campaign.prize_pool)}</span>
                        <span className="text-muted-foreground">{campaign.entries_count} entries</span>
                        <span className="text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(campaign.deadline).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </TabsContent>
              </Tabs>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="lg:col-span-1 mt-6 lg:mt-0 px-6 lg:px-0 space-y-6">
            {/* Revenue Split Info */}
            <div className="bg-card border border-white/10 rounded-xl p-5">
              <h3 className="font-semibold mb-4 flex items-center gap-2"><DollarSign className="w-5 h-5 text-primary" />How Earnings Work</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                  <span className="text-muted-foreground">Original Creator</span>
                  <span className="font-bold text-green-500">70-80%</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                  <span className="text-muted-foreground">Clipper</span>
                  <span className="font-bold text-blue-500">20-30%</span>
                </div>
                <p className="text-xs text-muted-foreground">Revenue splits are configurable per creator. Earnings based on views, engagement, and platform.</p>
              </div>
            </div>

            {/* Earnings Breakdown */}
            {earnings && (
              <div className="bg-card border border-white/10 rounded-xl p-5">
                <h3 className="font-semibold mb-4">Your Breakdown</h3>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">As Creator</span>
                    <span className="text-green-500 font-medium">{formatCurrency(earnings.as_creator)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">As Clipper</span>
                    <span className="text-blue-500 font-medium">{formatCurrency(earnings.as_clipper)}</span>
                  </div>
                  <div className="border-t border-white/10 pt-3 flex justify-between text-sm font-medium">
                    <span>Total</span>
                    <span>{formatCurrency(earnings.total_earnings)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Trending */}
            <div className="bg-card border border-white/10 rounded-xl p-5">
              <h3 className="font-semibold mb-4 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-primary" />Trending Clips</h3>
              <div className="space-y-3">
                {clips.filter(c => c.status === 'viral' || c.status === 'trending').slice(0, 5).map((clip, idx) => (
                  <div key={clip.clip_id} className="flex items-center gap-3">
                    <span className="text-lg font-bold text-muted-foreground w-6">{idx + 1}</span>
                    <img src={clip.thumbnail_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{clip.title}</p>
                      <p className="text-xs text-muted-foreground">{formatViews(clip.views)} views</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default ClipsPage;
